import React, { useState } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { addCurrency } from "../helpers/currencyFormat";

const frequencyTransfer = {
    daily: "day",
    monthly: "month",
    quarterly: "quarter",
    yearly: "year",
};

const GaborGrangerQuestions = ({ respondentId, type, gaborGranger, action }) => {
    const [answers, setAnswers] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(
        Math.floor(Math.random() * gaborGranger.prices.length)
    );
    const [surveyComplete, setSurveyComplete] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [visitedPrices, setVisitedPrices] = useState(new Set());

    const answer = {
        id: gaborGranger.id,
        respondent_id: respondentId,
        scale_answer: gaborGranger.scale_answer,
        currency: gaborGranger.currency,
        frequency: gaborGranger.frequency,
        unit: gaborGranger.unit,
        answers: answers
    }

    const surveyCompleted = () => {
        setSurveyComplete(true)


        // if (type === "survey") {
        //     console.log(answer)
        // }
    }

    const handleResponse = () => {
        if (!selectedAnswer) {
            alert("Please select an answer before proceeding.");
            return;
        }

        const currentPrice = gaborGranger.prices[currentIndex];
        setVisitedPrices((prev) => new Set(prev).add(currentPrice));

        // Record the current answer
        setAnswers((prevAnswers) => [
            ...prevAnswers,
            { price: currentPrice, response: selectedAnswer },
        ]);

        // Check if the survey should stop based on previous and current answers
        const lastAnswer = answers[answers.length - 1];
        if (
            lastAnswer?.response === "yes" &&
            (selectedAnswer === "no" || selectedAnswer <= 3)
        ) {
            surveyCompleted(); // Survey ends because the highest acceptable price is found
            return;
        }

        // Determine the next price index
        let nextIndex = currentIndex;
        if (selectedAnswer === "yes" || selectedAnswer >= 4) {
            // Move to the next higher price
            nextIndex = findNextIndex(currentIndex, 1);
        } else if (selectedAnswer === "no" || selectedAnswer <= 3) {
            // Move to the next lower price
            nextIndex = findNextIndex(currentIndex, -1);
        }

        // If no valid next index, end the survey
        if (nextIndex === -1) {
            surveyCompleted();
            return;
        }

        setCurrentIndex(nextIndex);
        setSelectedAnswer(null); // Reset the selected answer for the next question
    };

    const findNextIndex = (current, step) => {
        const prices = gaborGranger.prices;
        let index = current + step;
        while (index >= 0 && index < prices.length) {
            if (!visitedPrices.has(prices[index])) {
                return index;
            }
            index += step;
        }
        return -1; // No valid index found
    };

    const gaborGrangerQuestion = (price) => {
        const priceFormatted = addCurrency(price, gaborGranger.currency);
        const frequencyUnitFormatted =
            gaborGranger.frequency && gaborGranger.unit
                ? ` per ${frequencyTransfer[gaborGranger.frequency]} and ${gaborGranger.unit}`
                : gaborGranger.frequency
                    ? ` per ${frequencyTransfer[gaborGranger.frequency]}`
                    : gaborGranger.unit
                        ? ` per ${gaborGranger.unit}`
                        : "";
        const modifiedQuestion = gaborGranger.question.replace(
            "[Price]",
            priceFormatted + frequencyUnitFormatted
        );

        return <Typography>{modifiedQuestion}</Typography>;
    };

    const gaborGrangerAnswer = (scaleAnswer) => {
        const yesNoOptions = [
            { id: 1, value: "yes", label: "Yes" },
            { id: 2, value: "no", label: "No" },
        ];

        const scaleOptions = [
            { id: 1, value: 1, label: "Not at all likely" },
            { id: 2, value: 2, label: "Not likely" },
            { id: 3, value: 3, label: "Neither nor" },
            { id: 4, value: 4, label: "Likely" },
            { id: 5, value: 5, label: "Very likely" },
        ];

        const options = scaleAnswer ? scaleOptions : yesNoOptions;

        return (
            <FormControl>
                <RadioGroup
                    row={false}
                    value={selectedAnswer}
                    onChange={(event) => setSelectedAnswer(event.target.value)}
                >
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.id}
                            value={option.value}
                            control={<Radio />}
                            label={option.label}
                            disabled={surveyComplete}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
        );
    };

    return (
        <Box>
            <Stack direction="column">
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Product Description</Typography>
                    <Card sx={{ maxWidth: 500, minHeight: 100 }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {gaborGranger.product_description}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                {gaborGrangerQuestion(gaborGranger.prices[currentIndex])}
                {gaborGrangerAnswer(gaborGranger.scale_answer)}
                <Box>
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={handleResponse}
                        disabled={!selectedAnswer || surveyComplete}
                    >
                        Answer
                    </Button>
                </Box>

                <Box>
                    {surveyComplete ?
                        <React.Fragment>
                            {/* <Divider sx={{ mt: 5 }} /> */}
                            <Stack direction="row" alignItems="center" gap={2} sx={{mt: 4}}>
                                <Typography variant="body2">
                                    Question completed!
                                </Typography>
                                {action(answer)}
                            </Stack>
                        </React.Fragment>
                        : null}
                </Box>
            </Stack>
        </Box>
    );
};

export default GaborGrangerQuestions;
